import type { Theme, SxProps } from '@mui/material/styles';

import { Link } from 'react-router';
import React, { useState } from 'react';

import { Box, Stack, Button, Typography } from '@mui/material';

import { fDate } from 'src/utils/format-time';

import { getTime, getAgeInterval, formatDuration } from 'src/utils';
import {
  PLACEHOLDER_ACTIVITY_DATE_ISO,
  PLACEHOLDER_ACTIVITY_START_TIME,
} from 'src/constants/activity';

import { Iconify } from 'src/components/iconify';
import { ActivityDetailsDialog } from 'src/components/activity-details-dialog';

// ----------------------------------------------------------------------

type Props = {
  sx?: SxProps<Theme>;
  product: any;
  entityType: string;
};

export function ProductDetailsDescription({ product, entityType }: Props) {
  // Tarihi geçmiş aktivitede geçmiş günler de listelensin
  const isPast = entityType === 'aktivite' && Boolean(product?.isPast);
  const [openAllDates, setOpenAllDates] = useState(false);
  const [selectedDates, setSelectedDates] = useState([] as any);
  const [openLocationsDialog, setOpenLocationsDialog] = useState(false);
  const [openDatesDialog, setOpenDatesDialog] = useState(false);
  const [openTimesDialog, setOpenTimesDialog] = useState(false);

  return (
    <Stack sx={{ py: 3, px: 5 }}>
      <Typography
        variant="h6"
        fontFamily="Rubik"
        fontWeight={500}
        sx={{ color: 'text.secondary', textDecoration: 'underline', mb: 4 }}
      >
        Tanıtım
      </Typography>

      <Typography
        variant="body1"
        fontFamily="Rubik"
        fontWeight={400}
        sx={{ color: 'text.secondary', mb: 3, whiteSpace: 'pre-line' }}
      >
        {product?.listing?.description}
      </Typography>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
        <Typography
          variant="body1"
          fontFamily="Rubik"
          fontWeight={500}
          sx={{ color: 'primary.main' }}
        >
          {product?.organizer?.name}
        </Typography>
      </Stack>
      {/* <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
        <Typography
          variant="body1"
          fontFamily="Rubik"
          fontWeight={400}
          sx={{ color: 'text.secondary' }}
        >
          Kategori:
        </Typography>
        <Typography
          variant="body1"
          fontFamily="Rubik"
          fontWeight={500}
          sx={{ color: 'text.secondary' }}
        >
          {product.listing?.category}
        </Typography>
      </Stack> */}

      {(() => {
        const hasValidSchedule =
          product?.schedule?.startDate &&
          product.schedule.startDate !== PLACEHOLDER_ACTIVITY_DATE_ISO;

        const scheduleDays = product?.schedule?.days ?? [];
        const primaryDay = scheduleDays[0];
        const hasMultipleDays = scheduleDays.length > 1;

        // Çoklu aktivite kontrolü - eğer çoklu aktivite varsa butonları göster
        const hasMultipleDates = (product as any)?.hasMultipleDates;
        const hasMultipleTimes = (product as any)?.hasMultipleTimes;

        // Çoklu aktivite varsa direkt butonları göster
        if (hasMultipleDates || hasMultipleTimes || hasMultipleDays) {
          const renderTimeSuffix = () => {
            // hasMultipleTimes durumunda boş döndür, ayrı satırda gösterilecek
            if (hasMultipleTimes) {
              return '';
            }

            // Blog sayfalarında openHour/closeHour kontrolü
            if (entityType === 'blog' && product?.schedule?.openHour) {
              return ` - ${getTime(product.schedule.openHour)}${
                product.schedule.closeHour ? ` - ${getTime(product.schedule.closeHour)}` : ''
              }`;
            }

            const hasValidStartTime =
              primaryDay?.startTime != null &&
              primaryDay?.startTime !== PLACEHOLDER_ACTIVITY_START_TIME;

            if (!hasValidStartTime) {
              // Blog sayfalarında "Saat Sor" gösterilmemeli
              if (entityType === 'blog') {
                return '';
              }
              if (primaryDay?.startTime === PLACEHOLDER_ACTIVITY_START_TIME) {
                return ' - Saat Sor';
              }
              return '';
            }

            if (primaryDay?.startTime === 100) {
              return ` - ${getTime(primaryDay.startTime)}`;
            }

            if (hasValidStartTime) {
              return ` - ${getTime(primaryDay.startTime)}`;
            }

            return '';
          };

          return (
            <Stack direction="column" gap={1}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                <Iconify icon="solar:calendar-linear" width={24} sx={{ color: 'primary.main' }} />
                {hasMultipleDates ? (
                  <Button variant="text" sx={{ p: 0 }} onClick={() => setOpenDatesDialog(true)}>
                    <Typography variant="body1">Tarihleri Gör</Typography>
                  </Button>
                ) : hasMultipleDays ? (
                  <Button
                    variant="text"
                    sx={{ p: 0 }}
                    onClick={() => {
                      setSelectedDates(scheduleDays);
                      setOpenAllDates(true);
                    }}
                  >
                    <Typography variant="body1">Tarihleri Gör</Typography>
                  </Button>
                ) : hasValidSchedule ? (
                  <Typography
                    variant="body1"
                    fontFamily="Rubik"
                    fontWeight={400}
                    sx={{ color: 'text.secondary' }}
                  >
                    {`${fDate(product?.schedule?.startDate)}`}
                    {renderTimeSuffix()}
                  </Typography>
                ) : entityType === 'blog' ? null : (
                  <Typography
                    variant="body1"
                    fontFamily="Rubik"
                    fontWeight={400}
                    sx={{ color: 'text.secondary' }}
                  >
                    Tarih Sor
                  </Typography>
                )}
              </Stack>
              {hasMultipleTimes && (
                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                  <Iconify icon="tabler:clock" width={24} sx={{ color: 'warning.main' }} />
                  <Button
                    variant="text"
                    sx={{ p: 0, minWidth: 'auto' }}
                    onClick={() => setOpenTimesDialog(true)}
                  >
                    <Typography variant="body1">Saatleri Gör</Typography>
                  </Button>
                </Stack>
              )}
            </Stack>
          );
        }

        // Çoklu aktivite yoksa ve geçerli tarih yoksa
        // Blog sayfalarında hiçbir şey gösterme, aktivitelerde "Tarih Sor" göster
        if (!hasValidSchedule) {
          // Blog sayfalarında tarih/saat yoksa hiçbir şey gösterme
          if (entityType === 'blog') {
            return null;
          }

          return (
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
              <Iconify icon="solar:calendar-linear" width={24} sx={{ color: 'primary.main' }} />
              <Typography
                variant="body1"
                fontFamily="Rubik"
                fontWeight={400}
                sx={{ color: 'text.secondary' }}
              >
                Tarih Sor
              </Typography>
            </Stack>
          );
        }

        const renderTimeSuffix = () => {
          // Blog sayfalarında openHour/closeHour kontrolü
          if (entityType === 'blog' && product?.schedule?.openHour) {
            return ` - ${getTime(product.schedule.openHour)}${
              product.schedule.closeHour ? ` - ${getTime(product.schedule.closeHour)}` : ''
            }`;
          }

          const hasValidStartTime =
            primaryDay?.startTime != null &&
            primaryDay?.startTime !== PLACEHOLDER_ACTIVITY_START_TIME;

          // hasMultipleTimes durumunda boş döndür, ayrı satırda gösterilecek
          if ((product as any)?.hasMultipleTimes) {
            return '';
          }

          if (!hasValidStartTime) {
            // Blog sayfalarında "Saat Sor" gösterilmemeli
            if (entityType === 'blog') {
              return '';
            }
            if (primaryDay?.startTime === PLACEHOLDER_ACTIVITY_START_TIME) {
              return ' - Saat Sor';
            }
            return '';
          }

          if (primaryDay?.startTime === 100) {
            return ` - ${getTime(primaryDay.startTime)}`;
          }

          if (hasValidStartTime) {
            return ` - ${getTime(primaryDay.startTime)}`;
          }

          return '';
        };

        return (
          <Stack direction="column" gap={1}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
              <Iconify icon="solar:calendar-linear" width={24} sx={{ color: 'primary.main' }} />
              {(product as any)?.hasMultipleDates ? (
                <Button variant="text" sx={{ p: 0 }} onClick={() => setOpenDatesDialog(true)}>
                  <Typography variant="body1">Tarihleri Gör</Typography>
                </Button>
              ) : hasMultipleDays ? (
                <Button
                  variant="text"
                  sx={{ p: 0 }}
                  onClick={() => {
                    setSelectedDates(scheduleDays);
                    setOpenAllDates(true);
                  }}
                >
                  <Typography variant="body1">Tarihleri Gör</Typography>
                </Button>
              ) : (
                <Typography
                  variant="body1"
                  fontFamily="Rubik"
                  fontWeight={400}
                  sx={{ color: 'text.secondary' }}
                >
                  {`${fDate(product?.schedule?.startDate)}`}
                  {renderTimeSuffix()}
                </Typography>
              )}
            </Stack>
            {(product as any)?.hasMultipleTimes && (
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                <Iconify icon="tabler:clock" width={24} sx={{ color: 'warning.main' }} />
                <Button
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto' }}
                  onClick={() => setOpenTimesDialog(true)}
                >
                  <Typography variant="body1">Saatleri Gör</Typography>
                </Button>
              </Stack>
            )}
          </Stack>
        );
      })()}

      <Stack direction="row" alignItems="flex-start" gap={1} sx={{ mb: 0.5 }}>
        <Iconify
          icon="proicons:location"
          width={24}
          sx={{
            color: 'error.main',
            visibility: product?.listing?.format === 'online' ? 'hidden' : 'visible',
          }}
        />
        {product?.listing?.format === 'online' ? (
          <Box
            sx={{
              backgroundColor: 'success.main',
              py: 0.5,
              px: 1.5,
              borderRadius: 1,
              fontWeight: 'bold',
              color: 'white',
            }}
          >
            Online
          </Box>
        ) : (product as any)?.hasMultipleLocations ? (
          <Button
            variant="text"
            sx={{ p: 0, minWidth: 'auto' }}
            onClick={() => setOpenLocationsDialog(true)}
          >
            <Typography variant="body1">Konumları Gör</Typography>
          </Button>
        ) : (
          <Typography
            variant="body1"
            fontFamily="Rubik"
            fontWeight={400}
            sx={{ color: 'text.secondary' }}
          >
            <Box>
              {(() => {
                const title = product.location?.address?.title;
                const district = product.location?.district;
                const city = product.location?.city;

                const locationText =
                  district === city
                    ? city
                    : district && city
                      ? `${district}/${city}`
                      : district || city || '';

                return (
                  <>
                    {title && (
                      <Box component="span" display="block">
                        {title}
                      </Box>
                    )}
                    {locationText && (
                      <Box component="span" display="block">
                        {locationText}
                      </Box>
                    )}
                  </>
                );
              })()}
              {product?.location?.address?.description && (
                <Box component="span" display="block">
                  {product?.location?.address?.description}
                </Box>
              )}
            </Box>
          </Typography>
        )}
      </Stack>

      {/* Yaş aralığı - summary'deki gibi ayrı bir satır */}
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
        <Iconify icon="f7:person" width={24} sx={{ color: '#73B99A' }} />
        <Typography
          variant="body1"
          fontFamily="Rubik"
          fontWeight={400}
          sx={{ color: 'text.secondary' }}
        >
          {product?.listing?.ageInterval && product?.listing?.ageInterval.length > 0
            ? getAgeInterval(product?.listing?.ageInterval)
            : 'Tüm Yaşlar'}
        </Typography>
      </Stack>

      {/* Açılış-kapanış saati - summary'deki gibi ayrı bir satır */}
      {product?.schedule?.openHour && (
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
          <Iconify icon="tabler:clock" width={24} sx={{ color: 'warning.main' }} />
          <Typography
            variant="body1"
            fontFamily="Rubik"
            fontWeight={400}
            sx={{ color: 'text.secondary' }}
          >
            {`${getTime(product.schedule.openHour)}${
              product.schedule.closeHour ? ` - ${getTime(product.schedule.closeHour)}` : ''
            }`}
          </Typography>
        </Stack>
      )}

      {product?.duration && (
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
          <Typography variant="body1" fontFamily="Rubik" fontWeight={400} sx={{ color: '#CEC3BE' }}>
            Süre:
          </Typography>
          <Typography
            variant="body1"
            fontFamily="Rubik"
            fontWeight={400}
            sx={{ color: 'text.secondary' }}
          >
            {formatDuration(product.duration)}
          </Typography>
        </Stack>
      )}

      {product?.listing?.category && (
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
          <Typography variant="body1" fontFamily="Rubik" fontWeight={400} sx={{ color: '#CEC3BE' }}>
            Kategori:
          </Typography>
          {Array.isArray(product?.listing?.category) ? (
            product?.listing?.category.map((category: string, index: number) => (
              <React.Fragment key={index}>
                <Link to={`/aktivite?kategori=${category}`} style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body1"
                    fontFamily="Rubik"
                    fontWeight={400}
                    sx={{
                      color: 'text.secondary',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {category}
                  </Typography>
                </Link>
                {index < product.listing.category.length - 1 && (
                  <Typography
                    variant="body1"
                    fontFamily="Rubik"
                    fontWeight={400}
                    sx={{ color: 'text.secondary' }}
                  >
                    {' '}
                  </Typography>
                )}
              </React.Fragment>
            ))
          ) : (
            <Link
              to={`/aktivite?kategori=${product?.listing?.category}`}
              style={{ textDecoration: 'none' }}
            >
              <Typography
                variant="body1"
                fontFamily="Rubik"
                fontWeight={400}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {product?.listing?.category}
              </Typography>
            </Link>
          )}
        </Stack>
      )}

      {product?.quota && product?.quota !== 0 ? (
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
          <Typography variant="body1" fontFamily="Rubik" fontWeight={400} sx={{ color: '#CEC3BE' }}>
            Kontenjan:
          </Typography>
          <Typography
            variant="body1"
            fontFamily="Rubik"
            fontWeight={400}
            sx={{ color: 'text.secondary' }}
          >
            {product?.quota === 100000 ? 'Sınırsız' : product?.quota + ' kişi'}
          </Typography>
        </Stack>
      ) : null}
      {product?.listing?.companionship && (
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
          <Typography variant="body1" fontFamily="Rubik" fontWeight={400} sx={{ color: '#CEC3BE' }}>
            Veli Katılımı:
          </Typography>
          <Typography
            variant="body1"
            fontFamily="Rubik"
            fontWeight={400}
            sx={{ color: 'text.secondary' }}
          >
            {product?.listing?.companionship === 'required'
              ? 'Zorunlu'
              : product?.listing?.companionship === 'maybe'
                ? 'Zorunlu Değil'
                : 'Katılamaz'}
          </Typography>
        </Stack>
      )}
      {product?.location?.parking && product?.listing?.format !== 'online' && (
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
          <Typography variant="body1" fontFamily="Rubik" fontWeight={400} sx={{ color: '#CEC3BE' }}>
            Park Yeri:
          </Typography>
          <Typography
            variant="body1"
            fontFamily="Rubik"
            fontWeight={400}
            sx={{ color: 'text.secondary' }}
          >
            {product?.location?.parking === 'exists'
              ? 'Var'
              : product?.location?.parking === 'limited'
                ? 'Kısıtlı'
                : 'Yok'}
          </Typography>
        </Stack>
      )}
      {product?.hoursBeforeReservationExpires ? (
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 3.5 }}>
          <Typography variant="body1" fontFamily="Rubik" fontWeight={400} sx={{ color: '#CEC3BE' }}>
            Rezervasyonun Yapılabileceği Son Saat:
          </Typography>
          <Typography
            variant="body1"
            fontFamily="Rubik"
            fontWeight={400}
            sx={{ color: 'text.secondary' }}
          >
            {product?.hoursBeforeReservationExpires} saat önce
          </Typography>
        </Stack>
      ) : null}

      {product?.additionalDetails && (
        <Stack direction="column" gap={1} sx={{ mb: 0.5 }}>
          <Typography
            variant="body1"
            fontFamily="Rubik"
            fontWeight={600}
            sx={{ color: 'text.secondary' }}
          >
            Ek Bilgiler:
          </Typography>
          <Typography
            variant="caption"
            fontFamily="Rubik"
            fontWeight={400}
            sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}
          >
            {product?.additionalDetails}
          </Typography>
        </Stack>
      )}

      {/* Çoklu aktivite dialogları */}
      <ActivityDetailsDialog
        open={openLocationsDialog}
        onClose={() => setOpenLocationsDialog(false)}
        activities={(product as any)?.allActivities || []}
        displayType="locations"
        includePast={isPast}
      />
      <ActivityDetailsDialog
        open={openDatesDialog}
        onClose={() => setOpenDatesDialog(false)}
        activities={(product as any)?.allActivities || []}
        displayType="dates"
        includePast={isPast}
      />
      <ActivityDetailsDialog
        open={openTimesDialog}
        onClose={() => setOpenTimesDialog(false)}
        activities={(product as any)?.allActivities || []}
        displayType="times"
        includePast={isPast}
      />

      {/* Tek aktivite çoklu gün dialogu */}
      <ActivityDetailsDialog
        open={openAllDates}
        onClose={() => setOpenAllDates(false)}
        activities={
          selectedDates && selectedDates.length > 0 && selectedDates[0]?.date
            ? selectedDates
                .filter((date: any) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const dateToCheck = new Date(date.date);
                  dateToCheck.setHours(0, 0, 0, 0);
                  return isPast || dateToCheck >= today;
                })
                .map((date: any) => ({
                  ...product,
                  location: product?.location,
                  schedule: {
                    ...product?.schedule,
                    startDate: date.date,
                    days: [date],
                  },
                }))
            : product?.schedule?.days && product.schedule.days.length > 1
              ? (() => {
                  const startDate = new Date(product.schedule.startDate);
                  const activities: any[] = [];

                  product.schedule.days.forEach((day: any, idx: number) => {
                    const currentDate = new Date(startDate);
                    currentDate.setDate(startDate.getDate() + idx);

                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (isPast || currentDate >= today) {
                      activities.push({
                        ...product,
                        location: product?.location,
                        schedule: {
                          ...product?.schedule,
                          startDate: currentDate.toISOString(),
                          days: [day],
                        },
                      });
                    }
                  });

                  return activities;
                })()
              : product
                ? [product]
                : []
        }
        displayType="dates"
        includePast={isPast}
      />
    </Stack>
  );
}
